import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Link } from 'expo-router';

import { Search } from '@/components/icons/lucide';
// Design system components available but using original layout for now
import { VideoGridPlaceholder } from './placeholder';

import { PostInfo } from '@/components/md-blog';
import { VideoComponent } from '@/components/video-preview';
import { useIsLoadingUserData, useUser } from '@/states/auth';
import { useSignInModal } from '@/providers/modal/states';

const itemsPerPage = 8;

type VideoGridProps = {
  demos: PostInfo[];
};

const VideoContent = React.memo(
  ({ name, index }: { name: string; index: number }) => {
    // Wait for hero (1.2s) + search (0.7s) animations, then stagger grid items
    // Row-based stagger with diagonal wave effect
    const row = Math.floor(index / 4);
    const col = index % 4;
    const baseDelay = 1200; // After hero + search animations
    const enteringDelay = index > 7 ? 0 : baseDelay + row * 100 + col * 30;

    return (
      <div className="cursor-pointer md:scale-95 opacity-80 group-hover:[&:not(:hover)]:opacity-[0.75] hover:opacity-100 scale-100 duration-300 transition-all relative bg-surface-secondary border-transparent hover:border-primary flex hover:scale-[1.02] border-2 rounded-2xl">
        <VideoComponent name={name} enteringAnimationDelay={enteringDelay} />
      </div>
    );
  }
);

VideoContent.displayName = 'VideoContent';

// Memoized wrapper for unauthenticated users to avoid inline onClick
const VideoContentWithModal = React.memo(
  ({
    demo,
    index,
    onVideoClick,
  }: {
    demo: PostInfo;
    index: number;
    onVideoClick: (slug: string) => void;
  }) => {
    const handleClick = useCallback(() => {
      onVideoClick(demo.slug);
    }, [onVideoClick, demo.slug]);

    return (
      <div onClick={handleClick}>
        <VideoContent name={demo.slug} index={index} />
      </div>
    );
  }
);

VideoContentWithModal.displayName = 'VideoContentWithModal';

export const VideoGrid = React.memo(({ demos }: VideoGridProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItems, setCurrentItems] = useState<PostInfo[]>([]);
  const loaderRef = useRef<HTMLDivElement>(null);

  const user = useUser();
  const isLoadingUserData = useIsLoadingUserData();

  const signInModal = useSignInModal();

  const filteredPosts = useMemo(() => {
    if (!searchQuery) return demos;
    const query = searchQuery.toLowerCase();
    return demos.filter((post) => post.title.toLowerCase().includes(query));
  }, [demos, searchQuery]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentItems(filteredPosts.slice(0, itemsPerPage));
  }, [filteredPosts]);

  const handleObserver = useCallback(
    (entities: IntersectionObserverEntry[]) => {
      const [target] = entities;
      if (target.isIntersecting) {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    },
    []
  );

  useEffect(() => {
    const currentLoaderRef = loaderRef.current;
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(handleObserver, options);

    // Add a small delay to ensure the ref is properly set
    const timer = setTimeout(() => {
      if (currentLoaderRef) {
        observer.observe(currentLoaderRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
      observer.disconnect();
    };
  }, [handleObserver]);

  useEffect(() => {
    if (currentPage > 1) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const newItems = filteredPosts.slice(startIndex, endIndex);

      setCurrentItems((prevItems) => [...prevItems, ...newItems]);
    }
  }, [currentPage, filteredPosts]);

  const handleVideoClick = useCallback(
    (demoSlug: string) => {
      if (!user?.email) {
        signInModal.open(`/demos/${demoSlug}`);
        return;
      }
      // Demos are now free for all authenticated users
    },
    [user?.email, signInModal]
  );

  const isAuthenticated = Boolean(user?.email);

  const renderedListItems = useMemo(() => {
    return currentItems.map((demo, index) =>
      isAuthenticated ? (
        <Link
          key={demo.slug}
          href={`/demos/${demo.slug}`}
          className="group/card cursor-pointer transition-all duration-200 active:scale-[0.99] active:opacity-95 hover:bg-white/[0.04] hover:-translate-y-[2px]"
        >
          <VideoContent name={demo.slug} index={index} />
        </Link>
      ) : (
        <VideoContentWithModal
          key={demo.slug}
          demo={demo}
          index={index}
          onVideoClick={handleVideoClick}
        />
      )
    );
  }, [currentItems, handleVideoClick, isAuthenticated]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const showPlaceholder = currentItems.length === 0 && Boolean(searchQuery);

  return (
    <div className="mt-6 sm:mt-16">
      <div className="fade-in-down-4">
        <div className="mb-6 sm:mb-12 flex items-center justify-start sm:justify-center px-0 sm:px-0">
          <div className="relative w-full max-w-full sm:max-w-[400px] md:max-w-[500px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" />
            <input
              className="w-full pl-11 pr-4 py-2.5 sm:py-3 text-text-primary placeholder:text-text-tertiary rounded-xl border border-border-muted focus:border-border outline-none text-sm transition-colors duration-150 ease-out bg-surface-secondary/30 focus:bg-surface-secondary/50"
              placeholder="Search demos..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>
      {!isLoadingUserData && (
        <div className="animate-fade-in">
          {showPlaceholder ? (
            <VideoGridPlaceholder />
          ) : (
            <div className="grid gap-3 md:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 group animate-fade-in">
              {renderedListItems}
            </div>
          )}
        </div>
      )}
      <div
        ref={loaderRef}
        className="flex justify-center my-8 h-10"
        style={{ minHeight: '40px' }}
      />
    </div>
  );
});
